import React, { useState, useContext, useEffect } from "react";
import { MdClearAll } from "react-icons/md";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import {
	Avatar,
	Switch,
	FormControlLabel,
	Popover,
	Toolbar,
	AppBar,
	Typography,
	CircularProgress,
	Box,
	Button,
	IconButton
} from "@mui/material";
import { UserCard } from "../../components/Card";
import ConfirmationDialog from "../ConfirmationModals/ConfirmationDialog";

import { useCognitoContext } from "../../context/CognitoContext";
import { useAuthContext } from "../../context/AuthContext";
import UrlChecker from "../HealthCheck/UrlChecker";
import UpdateButton from "../UpdateButton";
import { useBackendStatusContext } from "../../context/backendStatus/backendStatusContext";
import { useAppContext } from "../../context/appContext";
import { useError } from "../../context/errorContext";
import DarkModeToggle from "./ToggleButton";
import { useTheme } from "@mui/material/styles";
import { StyledIconButton } from "../../constants/styledComponents";
import { formatName } from "../../utils/formatName";
import { useTranslation } from '../../context/TranslationContext';
import LanguageSelector from "../LanguageSelector";
import { useSidebar } from "../../context/SidebarContext";
import SoundControl from "../SoundControl/SoundControl";
import { SET_BACKEND_STATUS_REBOOTING } from "../../context/backendStatus/backendStatusActions";
import { usePollingManager } from "../../context/PollingManagerContext";

const Header = () => {
	const { translate } = useTranslation();
	const { signOut } = useCognitoContext();
	const { userData } = useAuthContext();
	const { toggleCompatibility, isLocalHost, toggleMockTest, togglePhoneBypass, isPhoneBypassed, isTestMocked } = useAppContext();
	const { toggleSidebar } = useSidebar();
	const backendStatus = useBackendStatusContext();
	const savedCompatibility = localStorage.getItem("compatibility")
	const [isRebootDialogOpen, setIsRebootDialogOpen] = useState(false);
	const [isShutdownDialogOpen, setIsShutdownDialogOpen] = useState(false);
	const [loadingReboot, setLoadingReboot] = useState(false);
	const [loadingShutdown, setLoadingShutdown] = useState(false);
	const [isModalClosing, setIsModalClosing] = useState(false);
	const { runningState } = usePollingManager();
	const isTestNotFailedAndNotReported = runningState?.isTestNotFailedAndNotReported ?? true;
	const backendIsRebooting = backendStatus.state.status === "rebooting";
	const isRebooting = localStorage.getItem("sonda.backend.rebooting") === "true";

	const { handleError } = useError();
	const theme = useTheme();

	const [state, setState] = useState({
		isOpenNotificationPopover: false,
		isNotificationConfirmed: false,
		isOpenUserCardPopover: false,
		currentUser: formatName(userData.firstName, userData.lastName),
	});

	useEffect(() => {
		if (!backendIsRebooting && !isRebooting) {
			setIsRebootDialogOpen(false);
			setLoadingReboot(false);
		}
	}, [backendIsRebooting, isRebooting]);

	const toggleNotificationPopover = () => {
		setState((prevState) => {
			return {
				...prevState,
				isOpenNotificationPopover: !prevState.isOpenNotificationPopover,
			};
		});

		if (state.isNotificationConfirmed) {
			setState((prevState) => {
				return {
					...prevState,
					isNotificationConfirmed: true,
				};
			});
		}
	};

	const csrfToken = localStorage.getItem("csrfToken");

	const handleReboot = async () => {
		try {
			setLoadingReboot(true);
			localStorage.setItem("sonda.backend.rebooting", "true");
			backendStatus.dispatch({ type: SET_BACKEND_STATUS_REBOOTING });
			await fetch(
				`${import.meta.env.VITE_APP_SERVER_URI}/${import.meta.env.VITE_APP_DATA_ACQUISITION_API_VERSION}/reboot`,
				{
					method: "GET",
					withCredentials: true,
					credentials: "include",
					headers: {
						"X-CSRF-Token": csrfToken,
					},
				}
			);

			console.log("Reboot request sent successfully");
		} catch (error) {
			console.error("Error sending reboot request", error);
			handleError(error);
		} finally {
			setIsModalClosing(true);
			// signOut();
		}
	};

	const handleShutdown = async () => {
		try {
			setLoadingShutdown(true);
			await fetch(
				`${import.meta.env.VITE_APP_SERVER_URI}/${import.meta.env.VITE_APP_DATA_ACQUISITION_API_VERSION}/shutdown`,
				{
					method: "GET",
					withCredentials: true,
					credentials: "include",
					headers: {
						"X-CSRF-Token": csrfToken,
					},
				}
			);

			console.log("Shutdown request sent successfully");
		} catch (error) {
			console.error("Error sending shutdown request", error);
			handleError(error);
		} finally {
			setIsModalClosing(true);
			signOut();
		}
	};

	const toggleUserCardPopover = () => {
		setState((prevState) => {
			return {
				...prevState,
				isOpenUserCardPopover: !prevState.isOpenUserCardPopover,
			};
		});
	};

	const handleSidebarControlButton = (event) => {
		event.preventDefault();
		event.stopPropagation();
		toggleSidebar();
	};

	const handleSwitchChange = (event) => {
		const isChecked = event.target.checked;
		toggleCompatibility(isChecked);
		location.reload();
	};

	const handleIsTestMockedSwitchToggle = (event) => {
		const isChecked = event.target.checked;
		toggleMockTest(isChecked);
	};

	const handleIsPhoneBypassedSwitchToggle = (event) => {
		const isChecked = event.target.checked;
		togglePhoneBypass(isChecked)

	}

	useEffect(() => {
		if (isModalClosing) {

			if (!loadingShutdown) {
				setIsShutdownDialogOpen(false);
				setLoadingShutdown(false);
			}

			// Only reset modal closing flag when both dialogs are cleared
			if (!isRebooting && !loadingShutdown) {
				setIsModalClosing(false);
			}
		}
	}, [isModalClosing, loadingShutdown]);

	return (
		<>
			<AppBar position="static" sx={{mb: 1}}>
				<Toolbar sx={{display: "flex", justifyContent: "space-between"}}>
					<div style={{ display: "flex", alignItems: "center" }}>
						<StyledIconButton onClick={handleSidebarControlButton}>
							<MdClearAll size={25} />
						</StyledIconButton>
						<DarkModeToggle />
						<SoundControl />
					</div>

					<div style={{ display: "flex", alignItems: "center" }}>
						<LanguageSelector />
						<div style={{ width: "1px", height: "24px", backgroundColor: "gray", marginRight: "10px" }}></div>
						<div style={{display: "flex", alignItems: "center"}}>
							{backendStatus.state.isUpdatePossible && <UpdateButton />}
							<UrlChecker
								url={`${import.meta.env.VITE_APP_SERVER_URI}/readiness`}
								reverse={true}
								circleSize={40}
								upAlias={translate("settings.deviceStatus.ready")}
								downAlias={translate("settings.deviceStatus.notReady")}
							/>
							<IconButton
								onClick={toggleUserCardPopover}
								aria-controls={state.isOpenUserCardPopover ? "user-card-popover" : undefined}
								aria-haspopup="true"
								aria-expanded={state.isOpenUserCardPopover ? "true" : undefined}
								size="small">
								<Avatar sx={{ cursor: "pointer" }} />
							</IconButton>
							<div>
								<Popover
									open={state.isOpenUserCardPopover}
									onClose={toggleUserCardPopover}
									anchorEl={document.getElementById("Popover2")}
									anchorOrigin={{ vertical: "top", horizontal: "right" }}
									transformOrigin={{ vertical: "top", horizontal: "right" }}
									PaperProps={{ style: { minWidth: 250, marginTop: "40px" } }}
								>
									<UserCard
										title={
											state.currentUser ? (
												state.currentUser
											) : (
												<span style={{ color: theme.palette.error.main }}>
													{translate('header.nameIsMissing')}
												</span>
											)
										}
										onSignOut={signOut}
										// text="Last updated 3 mins ago"
										text=""
									>
										<div
											style={{
												display: "flex",
												padding: "14px",
												gap: 5,
											}}
										>
											<Button
												startIcon={<RotateLeftIcon style={{ stroke: theme.palette.primary.main }} />}
												onClick={() => setIsRebootDialogOpen(true)}
												variant="outlined"
												style={{ fontSize: 14 }}
											>
												{translate('header.RebootDevice')}
											</Button>
                                            
                                            
											<Button
												startIcon={<PowerSettingsNewIcon style={{ stroke: theme.palette.primary.main }} />}
												onClick={() => setIsShutdownDialogOpen(true)}
												variant="outlined"
												style={{ fontSize: 14 }}
											>
												{translate('header.SwitchOffDevice')}
											</Button>
										</div>
									</UserCard>

									<div
										style={{
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
											flexDirection: "column",
										}}
									>
										{isLocalHost && (
											<div>
												<FormControlLabel
													control={
														<Switch
															onChange={handleSwitchChange}
															defaultChecked={JSON.parse(savedCompatibility)}
														/>
													}
													label={translate('header.BypassCompatibility')}
												/>

											</div>
										)}
										{isLocalHost && (
											<div>
												<FormControlLabel
													control={
														<Switch onChange={handleIsTestMockedSwitchToggle} defaultChecked={JSON.parse(isTestMocked)} />
													}
													label={translate('header.MockDataAcquisition')}
												/>
											</div>
										)}

										{isLocalHost && (
											<div>
												<FormControlLabel
													control={
														<Switch onChange={handleIsPhoneBypassedSwitchToggle} defaultChecked={JSON.parse(isPhoneBypassed)} />
													}
													label={translate('header.MockPhoneDetection')}
												/>
											</div>
										)}
									</div>
								</Popover>
							</div>
						</div>
					</div>
				</Toolbar>
			</AppBar>
			{(isRebootDialogOpen || backendIsRebooting) && (<ConfirmationDialog
				isOpen={isRebootDialogOpen || backendIsRebooting}
				onClose={() => {
					if (!isRebooting && !backendIsRebooting) {
						setIsRebootDialogOpen(false);
						setIsModalClosing(true);
					}
				}}
				onConfirm={handleReboot}
				title={
					loadingReboot || backendIsRebooting ? (
						<Box display="flex" justifyContent="center" alignItems="center">
							<Typography style={{ marginRight: '20px', fontSize: '22px' }}>
								{translate('header.RebootInProgress')}
							</Typography>
						</Box>
					) : (translate('header.RebootDevice'))
				}
				subtitle={loadingReboot || backendIsRebooting ? "" : translate('header.AreYouSureReboot')}
				warningMessage={
					!isTestNotFailedAndNotReported &&
					!loadingReboot &&
					!backendIsRebooting &&
					translate("header.rebootWillBeEnabled")}
				confirmDisabled={!isTestNotFailedAndNotReported}
				icon={isTestNotFailedAndNotReported ? <QuestionMarkIcon style={{ fontSize: "74px" }} /> : < PriorityHighIcon style={{ fontSize: "74px" }} />}
				inProgress={loadingReboot || backendIsRebooting}
			/>
			)}
			<ConfirmationDialog
				isOpen={isShutdownDialogOpen}
				onClose={() => {
					setIsShutdownDialogOpen(false);
					setIsModalClosing(true);
				}}
				onConfirm={handleShutdown}
				title={
					loadingShutdown ? (
						<Box display="flex" justifyContent="center" alignItems="center">
							<Typography style={{ fontSize: '20px' }}>
								{translate('header.SwitchOffInProgress')}
							</Typography>
						</Box>
					) : (translate('header.SwitchOffDevice'))
				}
				subtitle={loadingShutdown ? "" : translate('header.AreYouSureSwitchOff')}
				warningMessage={
					!isTestNotFailedAndNotReported &&
					!loadingShutdown &&
					translate("header.shutdownWillBeEnabled")}
				inProgress={loadingShutdown}
				confirmDisabled={!isTestNotFailedAndNotReported}
				icon={isTestNotFailedAndNotReported ? <QuestionMarkIcon style={{ fontSize: "74px" }} /> : < PriorityHighIcon fontSize="74px" style={{ fontSize: "74px" }} />}
			/>
			
		</>
	);
};

export default Header;
